import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  ApplySchedulePatternInput,
  CreateScheduleDraftInput,
  PublishScheduleInput,
  RemoveScheduleEntryInput,
  ScheduleValidationResult,
  StaffScheduleVersion,
  UpsertScheduleEntryInput,
} from "@bednbody/types";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";

export interface ScheduleStaff {
  id: string;
  name: string;
  role: "therapist" | "rider";
  active: boolean;
  hubId: string;
  hubName: string;
}

function mapVersion(
  row: Awaited<ReturnType<typeof fetchScheduleWeek>>[number],
): StaffScheduleVersion {
  return {
    id: row.id,
    hubId: row.hub_id,
    weekStart: row.week_start,
    versionNumber: row.version_number,
    sourceVersionId: row.source_version_id,
    changeReason: row.change_reason,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    publishedBy: row.published_by,
    publishedAt: row.published_at,
    status: row.published_at ? "published" : "draft",
    entries: row.staff_schedule_entries.map((entry) => ({
      id: entry.id,
      scheduleVersionId: entry.schedule_version_id,
      staffId: entry.staff_id,
      scheduleDate: entry.schedule_date,
      kind: entry.kind,
      dutyStart: entry.duty_start,
      dutyEnd: entry.duty_end,
      availabilityNote: entry.availability_note,
      createdAt: entry.created_at,
      updatedAt: entry.updated_at,
    })),
  };
}

async function fetchScheduleWeek(hubId: string, weekStart: string) {
  const { data, error } = await supabase
    .from("staff_schedule_versions")
    .select("*, staff_schedule_entries (*)")
    .eq("hub_id", hubId)
    .eq("week_start", weekStart)
    .order("version_number", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export function useScheduleStaff(hubId: string) {
  return useQuery({
    queryKey: ["schedule-staff", hubId],
    queryFn: async (): Promise<ScheduleStaff[]> => {
      const { data, error } = await supabase
        .from("staff")
        .select("id, full_name, role, active, hub_id, hubs ( name )")
        .eq("hub_id", hubId)
        .in("role", ["therapist", "rider"])
        .order("role")
        .order("full_name");
      if (error) throw error;
      return (data ?? []).flatMap((staff) =>
        staff.hub_id && (staff.role === "therapist" || staff.role === "rider")
          ? [
              {
                id: staff.id,
                name: staff.full_name,
                role: staff.role,
                active: staff.active === true,
                hubId: staff.hub_id,
                hubName: staff.hubs?.name ?? "Assigned hub",
              },
            ]
          : [],
      );
    },
    staleTime: 60_000,
  });
}

export function useScheduleWeek(hubId: string, weekStart: string) {
  return useQuery({
    queryKey: ["staff-schedules", hubId, weekStart],
    queryFn: async () => (await fetchScheduleWeek(hubId, weekStart)).map(mapVersion),
  });
}

function useScheduleMutation<TInput, TResult>(mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["staff-schedules"] }),
  });
}

export function useCreateScheduleDraft() {
  return useScheduleMutation<CreateScheduleDraftInput, StaffScheduleVersion>((input) =>
    callEdgeFunction("schedules/drafts", { data: input }),
  );
}

export function useUpsertScheduleEntry() {
  return useScheduleMutation<UpsertScheduleEntryInput, StaffScheduleVersion>((input) =>
    callEdgeFunction("schedules/drafts/entries", { method: "PUT", data: input }),
  );
}

export function useRemoveScheduleEntry() {
  return useScheduleMutation<RemoveScheduleEntryInput, StaffScheduleVersion>((input) =>
    callEdgeFunction("schedules/drafts/entries", { method: "DELETE", data: input }),
  );
}

export function useApplySchedulePattern() {
  return useScheduleMutation<ApplySchedulePatternInput, StaffScheduleVersion>((input) =>
    callEdgeFunction("schedules/drafts/patterns", { data: input }),
  );
}

export function useValidateScheduleDraft() {
  return useMutation({
    mutationFn: (input: { scheduleVersionId: string; expectedUpdatedAt: string }) =>
      callEdgeFunction<ScheduleValidationResult>("schedules/drafts/validate", { data: input }),
  });
}

export function usePublishSchedule() {
  return useScheduleMutation<PublishScheduleInput, StaffScheduleVersion>((input) =>
    callEdgeFunction("schedules/drafts/publish", { data: input }),
  );
}
